import React, { useEffect, useState } from 'react';
import '../Styles/Businesses.css';
import { Search } from '@mui/icons-material';
import { InputBase } from '@mui/material';
import { fetchAllBusinesses } from '../Functions';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { Helmet } from 'react-helmet';
import BusinessContactPopup from '../Utilities/ContactPopup/BusinessContactPopup';
import Bulb from '../Assets/Bulb.png'
function Businesses() {
  const analytics = getAnalytics();
  const [businesses, setBusinesses] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState('');

  const categories = [
    { name: 'Founder', isClickable: false, color: '#ffc0cb' },
    { name: 'Law & CA', isClickable: true },
    { name: 'Consulting', isClickable: true },
    { name: 'Financial Services', isClickable: true },
    { name: 'R & D', isClickable: true },
    { name: 'Office Space', isClickable: true },
    { name: 'Corporate vendors & Gifts', isClickable: true },
    { name: 'Publishing', isClickable: true },
    { name: 'HR', isClickable: true },
    { name: 'Branding, Marketing & Comm', isClickable: true },
    { name: 'IT Services', isClickable: true },
    { name: 'Design & Dashboards', isClickable: true },
    { name: 'Security', isClickable: true },
    { name: 'VC & Private Equities', isClickable: true },
    { name: 'Market Research', isClickable: true },
    { name: 'Strategy', isClickable: true }
  ];

  useEffect(() => {
    const getBusinesses = async () => {
      try {
        const fetchedBusinesses = await fetchAllBusinesses();
        setBusinesses(fetchedBusinesses);
        setFilteredData(fetchedBusinesses);
        localStorage.setItem('storedBusinesses', JSON.stringify(fetchedBusinesses));
        logEvent(analytics, 'Visit', { name: 'Businesses Page' });
      } catch (error) {
        console.error('Error fetching businesses:', error);
      }
    };

    getBusinesses();
  }, []);

  const handleSearchInputChange = (e) => {
    const input = e.target.value.toString().toLowerCase();
    setSearchInput(input);
    filterBusinesses(input, selectedCategory);
  };

  const handleCategoryClick = (categoryName) => {
    if (categoryName === 'Founder') return;
    
    const newCategory = selectedCategory === categoryName ? null : categoryName;
    setSelectedCategory(newCategory);
    filterBusinesses(searchInput, newCategory);
    
    // Log category click event
    if (newCategory) {
      logEvent(analytics, 'category_selected', { 
        category_name: categoryName 
      });
    }
  };

  const filterBusinesses = (searchText, category) => {
    let filtered = businesses;

    if (category) {
      filtered = filtered.filter(business => 
        business.category?.toLowerCase() === category.toLowerCase()
      );
    }

    if (searchText) {
      filtered = filtered.filter(business => {
        const searchableFields = [
          business.company,
          business.industry,
          business.notes,
          business.category,
          business.keywords
        ].filter(Boolean);
        
        return searchableFields.some(field =>
          field.toString().toLowerCase().includes(searchText)
        );
      });
    }

    setFilteredData(filtered);
  };

  const handleBusinessClick = (businessName) => {
    setSelectedBusiness(businessName);
    setIsPopupOpen(true);
    
    // Log business click event
    logEvent(analytics, 'business_clicked', { 
      business_name: businessName 
    });
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    setSelectedBusiness('');
  };

  return (
    <div className='Businesses-container'>
      <Helmet>
        <title>The Trehan | Business Search Engine</title>
        <meta name='description' content='Connect with The Trehan network of businesses. Search strategic partners for your next project.' data-react-helmet="true"/>
        <meta name="theme-color" content="#000000" data-react-helmet="true"/>
        <meta name='keywords' content="business network India, professional services India, business consulting, legal services, CA services, financial services, R&D partners, IT services India, HR consulting, market research, strategy consulting, business partnerships, corporate vendors, office space, security services, design services, private equity India, venture capital India, Mumbai businesses, Delhi businesses, Indian business network" data-react-helmet="true"/>
        <link rel='canonical' href='/businesses'/>
        
        <meta property="og:url" content="https://www.thetrehan.com/businesses"/>
        <meta property="og:type" content="website"/>
        <meta property="og:title" content="The Trehan Business Network"/>
        <meta property="og:description" content="Access The Trehan's curated network of businesses. Find strategic partners for your next project."/>
        <meta property="og:image" content="https://www.thetrehan.com/businesses-share.png"/>

        <meta name="twitter:card" content="summary_large_image"/>
        <meta property="twitter:domain" content="thetrehan.com"/>
        <meta property="twitter:url" content="https://www.thetrehan.com/businesses"/>
        <meta name="twitter:title" content="The Trehan Business Network"/>
        <meta name="twitter:description" content="Access The Trehan's curated network of businesses"/>
        <meta name="twitter:image" content="https://www.thetrehan.com/businesses-share.png"/>
      </Helmet>

      {/* Building Blocks - OUTSIDE Businesses-page */}
      <div className='Businesses-buildingBlocks'>
        <h3 className='Businesses-buildingBlocks-title'>Building blocks</h3>
        <div className='Businesses-buildingBlocks-grid'>
          {categories.map((category, index) => (
            <div
              key={index}
              className={`Businesses-block ${!category.isClickable ? 'non-clickable' : ''} ${
                selectedCategory === category.name ? 'active' : ''
              }`}
              style={{ backgroundColor: category.color || '' }}
              onClick={() => handleCategoryClick(category.name)}
            >
              <p className='Businesses-block-category'>{category.name}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Content - Separate from building blocks */}
      <div className='Businesses-page'>
        <div className='Businesses-content'>
          <div className='Businesses-header'>
            <h1 className='Businesses-title'>BUSINESS SEARCH ENGINE</h1>
            
            <div className='Businesses-searchBar'>
              <div className='Businesses-searchBar-deco'>
                <Search style={{ fontSize: '1.2rem' }} />
              </div>
              <InputBase
                sx={{ ml: 1, flex: 1 }}
                placeholder='Search'
                value={searchInput}
                onChange={handleSearchInputChange}
                inputProps={{ 'aria-label': 'search businesses' }}
              />
            </div>

            <p className='Businesses-description'>
              Utilise the strength of the Trehan network to quickly identify, connect with, and partner with the ideal member best suited to drive your next project forward.            
            </p>
          </div>

          <div className='Businesses-results'>
            {filteredData.length > 0 ? (
              filteredData.map((business) => (
                <div 
                  key={business.id} 
                  className='Businesses-result-item'
                  onClick={() => handleBusinessClick(business.company)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className='Businesses-result-icon'>
                    <img className='Businesses-result-icon-circle' src={Bulb} alt='Bulb'/>
                  </div>
                  <div className='Businesses-result-content'>
                    <h3 className='Businesses-result-company'>{business.company}</h3>
                    <p className='Businesses-result-industry'>{business.industry}</p>
                    {business.category && (
                      <p className='Businesses-result-category'>{business.category.toUpperCase()}</p>
                    )}
                    <p className='Businesses-result-notes'>{business.notes}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className='Businesses-no-results'>
                <p>No businesses found matching your search criteria.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Contact Popup */}
      <BusinessContactPopup 
        isOpen={isPopupOpen}
        onClose={closePopup}
        businessName={selectedBusiness}
      />
    </div>
  );
}

export default Businesses;